import React, { useEffect, useState } from "react";
import { FiDroplet, FiSettings } from "react-icons/fi";
import styles from "./Template.module.css";
import TemplateCard from "./TemplateCard";
import TemplateColorEditor from "./TemplateColorEditor";
import VisualEditor from "./VisualEditor";
import AxiosUrl from "../AxiosUrl";
import { toast } from "react-toastify";

function Template() {
  const [templates, setTemplates] = useState([]);
  const [storeTemplate, setStoreTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(null);
  const [editorTab, setEditorTab] = useState("visual"); // "visual" | "advanced"
  const uid = localStorage.getItem("id");

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [tplRes, storeRes] = await Promise.all([
        AxiosUrl.get("/templates"),
        AxiosUrl.get(`/store-template/${uid}`),
      ]);
      setTemplates(Array.isArray(tplRes.data) ? tplRes.data : []);
      setStoreTemplate(storeRes.data || null);
    } catch (err) {
      console.error("Failed to load templates:", err);
    } finally {
      setLoading(false);
    }
  }

  async function handleActivate(templateName, presetName) {
    setActivating(templateName);
    try {
      await AxiosUrl.post(`/set-store-template/${uid}`, {
        template: templateName,
        colorPreset: presetName || null,
      });
      toast.success("Template activated!");
      await loadData();
    } catch (err) {
      toast.error("Failed to activate template");
      console.error(err);
    } finally {
      setActivating(null);
    }
  }

  async function handleSaveColors(colors) {
    try {
      await AxiosUrl.post(`/set-store-template-colors/${uid}`, { colors });
      toast.success("Colors saved!");
      await loadData();
    } catch (err) {
      toast.error("Failed to save colors");
      console.error(err);
    }
  }

  const activeTemplateName = storeTemplate?.activeTemplate || "default";
  const activeTemplateDef = templates.find((t) => t.name === activeTemplateName);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Store Templates</h1>
        </div>
        <div className={styles.skeletonGrid}>
          {[...Array(6)].map((_, i) => (
            <div key={i} className={styles.skeletonCard} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Store Templates</h1>
        <p className={styles.subtitle}>
          Choose a template that matches your industry. Each template has unique
          designs optimized for Bangladeshi shoppers.
        </p>
        {activeTemplateName !== "default" && (
          <div className={styles.activeBanner}>
            Active: <strong>{activeTemplateDef?.label || activeTemplateName}</strong>
            {storeTemplate?.activeColorPreset && (
              <span> — {storeTemplate.activeColorPreset} preset</span>
            )}
          </div>
        )}
      </div>

      <div className={styles.grid}>
        {[...templates]
          .sort((a, b) => {
            // default first, cosmetics second, rest by label
            const order = (n) => (n === "default" ? 0 : n === "cosmetics" ? 1 : 2);
            const diff = order(a.name) - order(b.name);
            return diff !== 0 ? diff : (a.label || "").localeCompare(b.label || "");
          })
          .map((tpl) => (
            <TemplateCard
              key={tpl.name}
              template={tpl}
              isActive={activeTemplateName === tpl.name}
              isActivating={activating === tpl.name}
              onActivate={(presetName) => handleActivate(tpl.name, presetName)}
            />
          ))}
      </div>

      {activeTemplateDef && (
        <div className={styles.editorSection} id="template-editor">
          <div className={styles.editorHeading}>
            <div>
              <h2 className={styles.editorTitle}>
                Customize Your Storefront
              </h2>
              <p className={styles.editorSubtitle}>
                Editing <strong>{activeTemplateDef.label}</strong>
                {storeTemplate?.activeColorPreset ? ` · ${storeTemplate.activeColorPreset} preset` : ""}
                {" — changes preview live. Click Save to publish."}
              </p>
            </div>
          </div>
          <div className={styles.editorTabs}>
            <button
              className={`${styles.editorTab} ${editorTab === "visual" ? styles.editorTabActive : ""}`}
              onClick={() => setEditorTab("visual")}
            >
              <FiDroplet /> Visual Editor
            </button>
            <button
              className={`${styles.editorTab} ${editorTab === "advanced" ? styles.editorTabActive : ""}`}
              onClick={() => setEditorTab("advanced")}
            >
              <FiSettings /> Advanced (form)
            </button>
          </div>
          {editorTab === "visual" ? (
            <VisualEditor
              template={activeTemplateDef}
              storeTemplate={storeTemplate}
              uid={uid}
              onSave={handleSaveColors}
            />
          ) : (
            <TemplateColorEditor
              template={activeTemplateDef}
              storeTemplate={storeTemplate}
              onSave={handleSaveColors}
            />
          )}
        </div>
      )}

      {activeTemplateName !== "default" && (
        <div className={styles.resetSection}>
          <button
            className={styles.resetBtn}
            onClick={() => handleActivate("default")}
            disabled={activating === "default"}
          >
            {activating === "default" ? "Resetting..." : "Reset to Default Template"}
          </button>
        </div>
      )}
    </div>
  );
}

export default Template;
